import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { BrandVariant } from '@/lib/brandConfig';

interface QuoteModalContextType {
  isOpen: boolean;
  openQuoteModal: (options?: { category?: string; buttonLabel?: string }) => void;
  closeQuoteModal: () => void;
  preselectedCategory: string;
  buttonLabel: string;
}

const QuoteModalContext = createContext<QuoteModalContextType | undefined>(undefined);

interface QuoteModalProviderProps {
  children: ReactNode;
  siteKey: BrandVariant;
}

export const QuoteModalProvider = ({ children, siteKey }: QuoteModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [preselectedCategory, setPreselectedCategory] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');

  const openQuoteModal = useCallback((options?: { category?: string; buttonLabel?: string }) => {
    setPreselectedCategory(options?.category || '');
    setButtonLabel(options?.buttonLabel || 'Get Quote');
    setIsOpen(true);
  }, []);

  const closeQuoteModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Listen for custom event to open modal (used by savings modals)
  useEffect(() => {
    const handleOpenQuoteModal = (e: CustomEvent<{ category?: string; buttonLabel?: string }>) => {
      openQuoteModal(e.detail);
    };

    window.addEventListener('openQuoteModal', handleOpenQuoteModal as EventListener);
    return () => {
      window.removeEventListener('openQuoteModal', handleOpenQuoteModal as EventListener);
    };
  }, [openQuoteModal]);

  return (
    <QuoteModalContext.Provider value={{ 
      isOpen, 
      openQuoteModal, 
      closeQuoteModal, 
      preselectedCategory,
      buttonLabel 
    }}>
      {children}
    </QuoteModalContext.Provider>
  );
};

export const useQuoteModal = () => {
  const context = useContext(QuoteModalContext);
  if (!context) {
    throw new Error('useQuoteModal must be used within a QuoteModalProvider');
  }
  return context;
};
